/**
 * GET THE APP PANEL
 * =================
 * Shown to WEB users in place of the subscription paywall.
 * Web monetisation is disabled (2026-02-25) to eliminate trial abuse.
 * All Pro features live inside the native iOS/Android app.
 *
 * Props: onClose (optional), feature (optional Pro feature the user tapped)
 */
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Crown, Sparkles, X, Apple, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { openCustomerPortal } from '../services/stripeWebCheckout';
import { toast } from 'sonner';

const APP_STORE_URL = process.env.REACT_APP_APP_STORE_URL || 'https://apps.apple.com/app/tanzeen-plus/id0000000000';
const PLAY_STORE_URL = process.env.REACT_APP_PLAY_STORE_URL || 'https://play.google.com/store/apps/details?id=com.tanzeen.plus';

// Simple QR code via public generator (no bundle cost)
const QR_URL = `https://api.qrserver.com/v1/create-qr-code/?size=180x180&margin=0&data=${encodeURIComponent('https://tanzeenplus.com/download')}`;

export const GetTheAppPanel = ({ onClose, feature, fullScreen = true, showCloseButton = true }) => {
  const { t } = useTranslation();
  const [isRestoring, setIsRestoring] = useState(false);

  const proFeatures = useMemo(() => [
    { icon: '🎤', label: t('paywall.feature.tajweedCoach', 'Tajweed Coach AI - Real-time recitation feedback') },
    { icon: '🥗', label: t('paywall.feature.halalScanner', 'Halal Scanner - AI ingredient analysis') },
    { icon: '🕌', label: t('paywall.feature.masjidConnect', 'Masjid Connect - Find & sync with local mosques') },
    { icon: '🤲', label: t('paywall.feature.customDua', "Custom Du'a Generator - Personalized Duas") },
    { icon: '🎙️', label: t('paywall.feature.reciters', '20+ Premium Reciters') },
    { icon: '🌙', label: t('paywall.feature.nightVision', 'Night Vision Mode') },
  ], [t]);

  const handleRestore = async () => {
    const userId = localStorage.getItem('quranplus_user_id');
    if (!userId) {
      toast.info(t('getApp.noAccountToRestore', 'No previous purchases found on this device.'));
      return;
    }
    setIsRestoring(true);
    try {
      // Opens Stripe Customer Portal in a new tab for existing paying web customers
      await openCustomerPortal(userId);
    } catch (e) {
      console.error('[GetTheApp] portal open failed:', e);
      toast.error(t('getApp.portalUnavailable', 'Could not open subscription portal. Please contact support.'));
    } finally {
      setIsRestoring(false);
    }
  };

  return (
    <div
      className={`${fullScreen ? 'min-h-screen' : 'fixed inset-0 z-50'} bg-black/70 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto`}
      data-testid="get-the-app-panel"
    >
      <div className="w-full max-w-md my-auto py-4 relative">
        <Card className="relative overflow-hidden border-2 border-[#C9A227] shadow-2xl bg-[#F1EBDD]">
          {/* Ornate corner flourishes */}
          <div className="absolute top-2 left-2 w-6 h-6 pointer-events-none opacity-50 z-10">
            <svg viewBox="0 0 24 24" fill="none">
              <path d="M0 12 Q0 0, 12 0 M3 12 Q3 3, 12 3 M6 12 Q6 6, 12 6" stroke="#C9A227" strokeWidth="1" fill="none" strokeLinecap="round" />
            </svg>
          </div>
          <div className="absolute top-2 right-2 w-6 h-6 pointer-events-none opacity-50 z-10 scale-x-[-1]">
            <svg viewBox="0 0 24 24" fill="none">
              <path d="M0 12 Q0 0, 12 0 M3 12 Q3 3, 12 3 M6 12 Q6 6, 12 6" stroke="#C9A227" strokeWidth="1" fill="none" strokeLinecap="round" />
            </svg>
          </div>

          {/* Emerald Header */}
          <div className="bg-gradient-to-br from-[#0F4C3A] to-[#125845] px-5 py-4 text-[#F1EBDD] relative">
            {showCloseButton && (
              <Button
                variant="ghost"
                size="icon"
                onClick={onClose}
                className="absolute top-2 right-2 z-[60] text-[#F1EBDD]/80 hover:text-[#F1EBDD] bg-black/30 hover:bg-black/50 rounded-full w-8 h-8"
                data-testid="get-app-close-btn"
                aria-label="Close"
              >
                <X className="w-4 h-4" />
              </Button>
            )}
            <div className="flex flex-col items-center gap-1.5">
              <div className="p-2 bg-[#C9A227]/25 border-2 border-[#C9A227] rounded-xl shadow-inner">
                <Crown className="w-6 h-6 text-[#C9A227]" />
              </div>
              <h1 className="text-2xl font-bold font-serif text-[#F1EBDD] tracking-wide text-center leading-tight">
                {t('getApp.title', 'Get the Tanzeen+ App')}
              </h1>
              <p className="text-[#C9A227] italic text-[11px] text-center leading-tight px-2">
                {t('getApp.subtitle', 'Pro features live in the app. Download Tanzeen+ to unlock Tajweed Coach AI, Halal Scanner, Masjid Connect and more.')}
              </p>
              <div className="flex items-center justify-center gap-2 mt-1">
                <span className="h-px w-14 bg-gradient-to-r from-transparent to-[#C9A227]/60" />
                <Sparkles className="h-3 w-3 text-[#C9A227]" />
                <span className="h-px w-14 bg-gradient-to-l from-transparent to-[#C9A227]/60" />
              </div>
              {feature && feature.name && (
                <div className="inline-block px-3 py-0.5 bg-[#C9A227]/15 border border-[#C9A227]/60 rounded-full mt-1">
                  <span className="text-[#C9A227] text-[11px]">
                    <span className="font-semibold">{feature.name}</span> {t('getApp.availableInApp', 'available in the app')}
                  </span>
                </div>
              )}
            </div>
          </div>

          <CardContent className="p-4 bg-[#F1EBDD] space-y-4">
            {/* Store buttons */}
            <div className="flex flex-col gap-2.5">
              <a
                href={APP_STORE_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="app-store-btn"
                className="flex items-center justify-center gap-2 w-full py-3 rounded-xl bg-[#0F4C3A] hover:bg-[#125845] text-[#F1EBDD] font-semibold transition-colors shadow-md"
              >
                <Apple className="w-5 h-5" />
                <span>{t('getApp.downloadAppStore', 'Download on the App Store')}</span>
              </a>
              <a
                href={PLAY_STORE_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="play-store-btn"
                className="flex items-center justify-center gap-2 w-full py-3 rounded-xl bg-[#0F4C3A] hover:bg-[#125845] text-[#F1EBDD] font-semibold transition-colors shadow-md"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                  <path d="M3.6 1.5c-.4.3-.6.8-.6 1.4v18.2c0 .6.2 1.1.6 1.4l10.5-10.5L3.6 1.5zm12 8.5L18.9 12l-3.3 2 3.3-2-3.3-2zm-1.4 1.4L4.4 22.4c.3.1.7.1 1 0l11.3-6.4-2.5-2.6zm0-2.8l2.5-2.6L5.4 1.6c-.3-.1-.7-.1-1 0l9.8 9.8v-.8z"/>
                </svg>
                <span>{t('getApp.downloadPlayStore', 'Get it on Google Play')}</span>
              </a>
            </div>

            {/* Divider + QR */}
            <div className="flex items-center gap-3">
              <span className="flex-1 h-px bg-[#C9A227]/40" />
              <span className="text-[10px] uppercase tracking-widest text-[#4A4A4A] font-semibold">
                {t('getApp.orScan', 'or scan')}
              </span>
              <span className="flex-1 h-px bg-[#C9A227]/40" />
            </div>

            <div className="flex flex-col items-center gap-2">
              <div className="p-2 bg-white rounded-lg border-2 border-[#C9A227]/40 shadow-inner">
                <img
                  src={QR_URL}
                  alt={t('getTheApp.qrAlt', "Download Tanzeen+ QR code")}
                  className="w-32 h-32"
                  loading="lazy"
                  data-testid="get-app-qr"
                />
              </div>
              <p className="text-[10px] text-[#4A4A4A] text-center">
                {t('getApp.scanHint', 'Point your phone camera at the QR to install')}
              </p>
            </div>

            {/* Feature reminder */}
            <div className="border-t border-[#C9A227]/30 pt-3">
              <p className="text-[10px] uppercase tracking-widest text-[#0F4C3A] font-semibold text-center mb-2">
                {t('getApp.includedInApp', 'Included in the app')}
              </p>
              <ul className="grid grid-cols-2 gap-1.5">
                {proFeatures.map((f, i) => (
                  <li key={i} className="flex items-start gap-1.5 text-[10px] text-[#0F4C3A] leading-tight">
                    <span className="text-sm leading-none" aria-hidden="true">{f.icon}</span>
                    <span className="flex-1">{f.label}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Restore link for existing paying web customers */}
            <div className="pt-2 text-center">
              <button
                type="button"
                onClick={handleRestore}
                disabled={isRestoring}
                data-testid="restore-web-purchase-btn"
                className="text-[11px] text-[#0F4C3A]/80 hover:text-[#0F4C3A] underline underline-offset-2 inline-flex items-center gap-1.5"
              >
                {isRestoring && <Loader2 className="w-3 h-3 animate-spin" />}
                {t('getApp.restore', 'Already subscribed? Manage subscription')}
              </button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default GetTheAppPanel;
